import Icon from '@cobliteam/ui/icons';
import { cn } from '@cobliteam/ui/utils';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@cobliteam/ui-business/components/Collapsible';
import { useControllableState } from '@radix-ui/react-use-controllable-state';
import type { ComponentProps, ReactNode } from 'react';
import { createContext, memo, useContext, useEffect, useState } from 'react';
import remarkGfm from 'remark-gfm';
import { Streamdown } from 'streamdown';
import { Shimmer } from './Shimmer';

interface ReasoningContextValue {
  isStreaming: boolean;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  duration: number | undefined;
}

const ReasoningContext = createContext<ReasoningContextValue | null>(null);

export const useReasoning = () => {
  const context = useContext(ReasoningContext);
  if (!context) {
    throw new Error('Reasoning components must be used within Reasoning');
  }
  return context;
};

export type ReasoningProps = ComponentProps<typeof Collapsible> & {
  isStreaming?: boolean;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  duration?: number;
};

const AUTO_CLOSE_DELAY = 3000;
const MS_IN_S = 1000;

export const Reasoning = memo(({ className, isStreaming = false, open, defaultOpen = false, onOpenChange, duration: durationProp, children, ...props }: ReasoningProps) => {
  const [isOpen, setIsOpen] = useControllableState({
    prop: open,
    defaultProp: defaultOpen,
    onChange: onOpenChange,
  });
  const [duration, setDuration] = useControllableState({
    prop: durationProp,
    defaultProp: undefined,
  });

  const [hasAutoClosed, setHasAutoClosed] = useState(false);
  const [startTime, setStartTime] = useState<number | null>(null);

  // Track duration when streaming starts and ends
  useEffect(() => {
    if (isStreaming) {
      if (startTime === null) {
        setStartTime(Date.now());
      }
    } else if (startTime !== null) {
      setDuration(Math.ceil((Date.now() - startTime) / MS_IN_S));
      setStartTime(null);
    }
  }, [isStreaming, startTime, setDuration]);

  // Auto-open when streaming starts, auto-close when streaming ends (once only)
  useEffect(() => {
    if (defaultOpen && !isStreaming && isOpen && !hasAutoClosed) {
      // Add a small delay before closing to allow user to see the content
      const timer = setTimeout(() => {
        setIsOpen(false);
        setHasAutoClosed(true);
      }, AUTO_CLOSE_DELAY);

      return () => clearTimeout(timer);
    }
  }, [isStreaming, isOpen, defaultOpen, setIsOpen, hasAutoClosed]);

  const handleOpenChange = (newOpen: boolean) => {
    setIsOpen(newOpen);
  };

  return (
    <ReasoningContext.Provider value={{ isStreaming, isOpen, setIsOpen, duration }}>
      <Collapsible className={cn('not-prose h-auto', className)} onOpenChange={handleOpenChange} open={isOpen} {...props}>
        {children}
      </Collapsible>
    </ReasoningContext.Provider>
  );
});

export type ReasoningTriggerProps = ComponentProps<typeof CollapsibleTrigger> & {
  getThinkingMessage?: (isStreaming: boolean, duration?: number) => ReactNode;
};

const defaultGetThinkingMessage = (isStreaming: boolean, duration?: number) => {
  if (isStreaming || duration === 0) {
    return <Shimmer duration={1}>Pensando...</Shimmer>;
  }
  if (duration === undefined) {
    return <p>Pensado por alguns segundos</p>;
  }
  return <p>Pensando por {duration} segundos</p>;
};

export const ReasoningTrigger = memo(({ className, children, getThinkingMessage = defaultGetThinkingMessage, ...props }: ReasoningTriggerProps) => {
  const { isStreaming, isOpen, duration } = useReasoning();

  return (
    <CollapsibleTrigger className={cn('mt-4 flex w-full items-center gap-2 text-muted-foreground text-sm transition-colors hover:text-foreground', className)} {...props}>
      {children ?? (
        <>
          <Icon name='brain' size='sm' color='gray' />
          {getThinkingMessage(isStreaming, duration)}
          <Icon name='chevron-down' size='sm' className={cn('transition-transform', isOpen ? 'rotate-180' : 'rotate-0')} />
        </>
      )}
    </CollapsibleTrigger>
  );
});

export type ReasoningContentProps = ComponentProps<typeof CollapsibleContent> & {
  children: string;
};

export const ReasoningContent = memo(({ className, children, ...props }: ReasoningContentProps) => (
  <CollapsibleContent
    className={cn(
      'mt-4 rounded-lg border border-gray-4 bg-gray-2 px-4 text-sm',
      'data-[state=closed]:fade-out-0 data-[state=closed]:slide-out-to-top-2 data-[state=open]:slide-in-from-top-2 outline-none data-[state=closed]:animate-out data-[state=open]:animate-in',
      className
    )}
    {...props}
  >
    <Streamdown
      className='!text-xs space-y-0 px-2 py-4'
      remarkPlugins={[remarkGfm]}
      isAnimating
      mode='streaming'
      components={{
        hr: () => <hr className='border-gray-4' />,
        ul: (props) => <ul {...(props as ComponentProps<'ul'>)} className={cn('list-disc pl-6 text-gray-11 hover:text-gray-11', (props as ComponentProps<'ul'>).className)} />,
        ol: (props) => <ol {...(props as ComponentProps<'ol'>)} className={cn('list-decimal pl-6 text-gray-11 hover:text-gray-11', (props as ComponentProps<'ol'>).className)} />,
        li: (props) => <li {...(props as ComponentProps<'li'>)} className={cn('my-1.5 text-gray-11 hover:text-gray-11', (props as ComponentProps<'li'>).className)} />,
        table: ({ children }) => (
          <div className='my-4 overflow-hidden rounded-lg border border-gray-6'>
            <table className='w-full border-collapse text-sm'>{children}</table>
          </div>
        ),
        thead: (props) => <thead {...(props as ComponentProps<'thead'>)} className='bg-gray-2 text-gray-12' />,
        th: (props) => <th {...(props as ComponentProps<'th'>)} className='border-gray-6 border-r border-b px-4 py-2.5 text-left font-semibold last:border-r-0' />,
        td: (props) => <td {...(props as ComponentProps<'td'>)} className='border-gray-4 border-r border-b px-4 py-2 text-gray-11 last:border-r-0' />,
        tr: (props) => <tr {...(props as ComponentProps<'tr'>)} className='transition-colors last:border-b-0 hover:bg-gray-1' />,
        a: (props) => <a {...(props as ComponentProps<'a'>)} className='font-medium text-link hover:text-link hover:underline' />,
        strong: (props) => <strong {...(props as ComponentProps<'strong'>)} className='font-semibold text-gray-11' />,
        code: (props) => <code {...(props as ComponentProps<'code'>)} className='rounded-md bg-gray-2 px-1 py-0.5 text-gray-11' />,
      }}
    >
      {children}
    </Streamdown>
  </CollapsibleContent>
));

Reasoning.displayName = 'Reasoning';
ReasoningTrigger.displayName = 'ReasoningTrigger';
ReasoningContent.displayName = 'ReasoningContent';
